import { isSameDay, isSameMonth, isSameYear, parse } from 'date-fns'

import { CustomerSort, CustomerSortName } from '../constants/customer'
import {
  DATA_EXPORT_SPECIAL_QUERY_KEYS,
  SALE_INVENTORY_TYPE_OPTIONS,
  SALE_PAYMENT_TYPE_OPTIONS,
  SALE_STATUS_OPTIONS,
} from '../constants/dataExportQuery'
import { ReportType } from '../constants/report'
import { formatUTCDate } from './dateUtils'

export type DataExportQuery = Record<string, unknown>

type DataExportQuerySummaryOptions = {
  reportType?: ReportType | null
  venueName?: string
  venueNamesById?: Record<string, string>
  eventName?: string
}

const parseISOStringWithoutTimezone = (date: string): Date => {
  return parse(date, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", new Date())
}

export function isEmptyDataExportQueryValue(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (value === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

export function toDataExportDateString(value: unknown): string | null {
  if (value == null) return null
  if (typeof value === 'string') return value
  if (typeof value === 'number') return new Date(value).toISOString()
  if (value instanceof Date) return value.toISOString()
  if (Array.isArray(value) && value.length > 0)
    return toDataExportDateString(value[0])
  return null
}

function formatDateValue(value: unknown): string {
  const dateString = toDataExportDateString(value)
  if (!dateString) return ''
  try {
    return formatUTCDate({ date: dateString, formatStr: 'MMM do, yyyy' })
  } catch {
    return dateString
  }
}

function getDateRangeLabel(dateStart?: string, dateEnd?: string): string {
  let dateLabel = ''

  if (dateStart && dateEnd) {
    dateLabel = 'Date'

    const rangeStart = parseISOStringWithoutTimezone(dateStart)
    const rangeEnd = parseISOStringWithoutTimezone(dateEnd)

    if (isSameDay(rangeStart, rangeEnd)) {
      dateLabel = formatUTCDate({
        date: dateStart,
        formatStr: 'EEE, MMM do, yyyy',
      })
    } else if (isSameMonth(rangeStart, rangeEnd)) {
      dateLabel = `${formatUTCDate({ date: dateStart, formatStr: 'MMM do' })} - ${formatUTCDate({ date: dateEnd, formatStr: 'do, yyyy' })}`
    } else if (isSameYear(rangeStart, rangeEnd)) {
      dateLabel = `${formatUTCDate({ date: dateStart, formatStr: 'MMM do' })} - ${formatUTCDate({ date: dateEnd, formatStr: 'MMM do, yyyy' })}`
    } else {
      dateLabel = `${formatUTCDate({ date: dateStart, formatStr: 'MMM do, yyyy' })} - ${formatUTCDate({ date: dateEnd, formatStr: 'MMM do, yyyy' })}`
    }
  } else if (dateStart) {
    dateLabel = formatUTCDate({ date: dateStart, formatStr: 'MMM do, yyyy' })
  } else if (dateEnd) {
    dateLabel = formatUTCDate({ date: dateEnd, formatStr: 'MMM do, yyyy' })
  }

  if (dateLabel === '') {
    dateLabel = 'Date'
  }

  return dateLabel
}

export function getDataExportQuerySummaryLines(
  query: DataExportQuery,
  options: DataExportQuerySummaryOptions = {}
): string[] {
  const lines: string[] = []
  const keys = Object.keys(query)
  if (keys.length === 0) {
    return ['No filters']
  }

  if ('venueId' in query) {
    const raw = query.venueId
    if (!isEmptyDataExportQueryValue(raw)) {
      const ids = Array.isArray(raw)
        ? raw.filter((item): item is string => typeof item === 'string')
        : typeof raw === 'string'
          ? [raw]
          : []
      if (ids.length > 0) {
        const venueLabel = options.venueName
          ? options.venueName
          : ids.map((id) => options.venueNamesById?.[id] ?? id).join(', ')
        lines.push(`Venues: ${venueLabel}`)
      }
    }
  }

  if ('customerId' in query) {
    const raw = query.customerId
    if (!isEmptyDataExportQueryValue(raw)) {
      const values = Array.isArray(raw) ? raw : [raw]
      const count = values.length
      if (count > 0) {
        lines.push(count === 1 ? '1 customer' : `${count} customers`)
      }
    }
  }

  if ('customerTagId' in query) {
    const raw = query.customerTagId
    if (!isEmptyDataExportQueryValue(raw)) {
      const values = Array.isArray(raw) ? raw : [raw]
      const count = values.length
      if (count > 0) {
        lines.push(count === 1 ? '1 tag' : `${count} tags`)
      }
    }
  }

  if ('sort' in query) {
    const raw = query.sort
    if (typeof raw === 'string') {
      const isCustomerExport =
        options.reportType === ReportType.TLPRO_CUSTOMER_DUMP
      const sortEntry = Object.entries(CustomerSort).find(
        ([, value]) => value === raw
      )?.[0] as keyof typeof CustomerSortName | undefined
      if (isCustomerExport && sortEntry && sortEntry in CustomerSortName) {
        lines.push(`Sort: ${CustomerSortName[sortEntry]}`)
      } else {
        const isDesc = raw.startsWith('-')
        const field = isDesc ? raw.slice(1) : raw
        const direction = isDesc ? 'descending' : 'ascending'
        lines.push(`Sort: ${field}, ${direction}`)
      }
    }
  }

  const dateStart = toDataExportDateString(query.dateStart)
  const dateEnd = toDataExportDateString(query.dateEnd)
  const saleDateStart = toDataExportDateString(query.saleDateStart)
  const saleDateEnd = toDataExportDateString(query.saleDateEnd)
  const exportDateStart = toDataExportDateString(query.exportDateStart)
  const exportDateEnd = toDataExportDateString(query.exportDateEnd)
  const singleDate = toDataExportDateString(query.date)

  if (dateStart || dateEnd) {
    const label = getDateRangeLabel(
      dateStart ?? undefined,
      dateEnd ?? undefined
    )
    if (label && label !== 'Date') {
      lines.push(`Date range: ${label}`)
    }
  } else if (singleDate) {
    const formatted = formatDateValue(query.date)
    if (formatted) {
      lines.push(`Date: ${formatted}`)
    }
  }

  if (exportDateStart || exportDateEnd) {
    const label = getDateRangeLabel(
      exportDateStart ?? undefined,
      exportDateEnd ?? undefined
    )
    if (label && label !== 'Date') {
      lines.push(`Export date range: ${label}`)
    }
  }

  if (saleDateStart || saleDateEnd) {
    const label = getDateRangeLabel(
      saleDateStart ?? undefined,
      saleDateEnd ?? undefined
    )
    if (label && label !== 'Date') {
      lines.push(`Sales date range: ${label}`)
    }
  }

  if ('hasPaymentProviderAccountId' in query) {
    const raw = query.hasPaymentProviderAccountId
    if (raw === true || raw === 'true') {
      lines.push('Sales type: Stripe Connect')
    } else if (raw === false || raw === 'false') {
      lines.push('Sales type: Tablelist')
    }
  }

  if ('status' in query) {
    const raw = query.status
    if (typeof raw === 'string' && raw.length > 0) {
      const label =
        SALE_STATUS_OPTIONS.find((option) => option.value === raw)?.label ?? raw
      lines.push(`Status: ${label}`)
    }
  }

  if ('inventoryType' in query) {
    const raw = query.inventoryType
    const values = Array.isArray(raw) ? raw : raw != null ? [raw] : []
    if (values.length > 0) {
      const label = SALE_INVENTORY_TYPE_OPTIONS.find(
        (option) =>
          'queryValue' in option &&
          JSON.stringify(option.queryValue) === JSON.stringify(values)
      )?.label
      lines.push(`Inventory type: ${label ?? values.join(', ')}`)
    }
  }

  if ('paymentType' in query) {
    const raw = query.paymentType
    const values = Array.isArray(raw) ? raw : raw != null ? [raw] : []
    if (values.length > 0) {
      const label = SALE_PAYMENT_TYPE_OPTIONS.find(
        (option) =>
          'queryValue' in option &&
          JSON.stringify(option.queryValue) === JSON.stringify(values)
      )?.label
      lines.push(`Payment type: ${label ?? values.join(', ')}`)
    }
  }

  if ('eventId' in query) {
    const raw = query.eventId
    if (!isEmptyDataExportQueryValue(raw) && typeof raw === 'string') {
      lines.push(`Event: ${options.eventName ?? raw}`)
    }
  }

  if ('cityId' in query) {
    const raw = query.cityId
    if (!isEmptyDataExportQueryValue(raw) && typeof raw === 'string') {
      lines.push(`City: ${raw}`)
    }
  }

  if ('affiliateId' in query) {
    const raw = query.affiliateId
    if (!isEmptyDataExportQueryValue(raw) && typeof raw === 'string') {
      lines.push(`Affiliate: ${raw}`)
    }
  }

  return lines
}

export function getDataExportRemainingQuery(
  query: DataExportQuery
): Record<string, unknown> {
  const known = new Set<string>(DATA_EXPORT_SPECIAL_QUERY_KEYS)
  return Object.fromEntries(
    Object.entries(query).filter(
      ([key, value]) => !known.has(key) && !isEmptyDataExportQueryValue(value)
    )
  )
}
